<template>
  <div class="ingredients-spoiler flex flex-col gap-30">
    <article
      v-for="(item, index) in sorted"
      :key="index"
      class="ingredients-spoiler__card overflow-hidden rounded-[10px] bg-white"
    >
      <header
        v-if="cardTitle"
        class="ingredients-spoiler__header bg-coral px-30 pt-[9px] pb-10 text-center rounded-t-[10px]"
      >
        <h3 class="font-degular text-h2 max-md:text-h3-mobile font-medium leading-none text-white">
          {{ cardTitle }}
        </h3>
      </header>

      <div class="ingredients-spoiler__body flex gap-30 px-30 py-30 md:grid-cols-2">
        <div class="ingredients-spoiler__media flex-[0_1_47%] flex flex-col items-center text-center">
          <span
            v-if="item?.title"
            class="ingredients-spoiler__name font-degular h4 font-medium capitalize text-coral mb-5"
          >
            {{ item.title }}
          </span>
          <img
            v-if="item?.url"
            :src="item.url"
            :alt="item?.title"
            class="ingredients-spoiler__image"
          />
        </div>

        <div class="ingredients-spoiler__sections flex-[0_1_53%] flex flex-col">
          <section
            v-for="(section, sIndex) in sectionsFor(item)"
            :key="sIndex"
            class="ingredients-spoiler__section border-t-[2px] border-coral last:border-b-[2px]"
          >
            <button
              type="button"
              class="ingredients-spoiler__toggle flex w-full items-center justify-between py-5 text-left font-degular h6 font-semibold uppercase text-coral"
              :aria-expanded="isOpen(index, sIndex)"
              @click="toggle(index, sIndex)"
            >
              <span>{{ section.title }}</span>
              <span class="ingredients-spoiler__icon ml-10 flex-shrink-0">
                <svg
                  v-if="!isOpen(index, sIndex)"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path d="M8 0L8 16" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                  <path d="M16 8L0 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
                <svg
                  v-else
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="4"
                  viewBox="0 0 16 4"
                  fill="none"
                >
                  <path d="M16 2L0 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
              </span>
            </button>
            <div
              v-show="isOpen(index, sIndex)"
              class="ingredients-spoiler__content pb-15 flex flex-col gap-10 text-body-sm text-black"
            >
              <p v-for="(line, lIndex) in section.body" :key="lIndex">
                {{ line }}
              </p>
            </div>
          </section>
        </div>
      </div>
    </article>
  </div>
</template>

<script>
export default {
  name: "IngredientsSpoiler",
  props: {
    tags: {type: Array, default: () => []},
    orders: {type: String, default: ""},
    cardTitle: {type: String, default: ""},
    section1Title: {type: String, default: ""},
    section2Title: {type: String, default: ""},
  },
  data() {
    return {open: {}};
  },
  computed: {
    sorted() {
      const valid = this.tags.filter(t => t && Object.keys(t).length > 0);
      return valid.length ? this.sortByOrders(valid) : valid;
    },
  },
  mounted() {
    const initial = {};
    this.sorted.forEach((_, idx) => {
      initial[idx] = [0];
    });
    this.open = initial;
  },
  methods: {
    sectionsFor(item) {
      const children = item?.children || [];
      const groups = [
        {title: this.section1Title, range: [0, 2]},
        {title: this.section2Title, range: [2, 7]},
      ];
      return groups
        .map(({title, range}) => ({
          title,
          body: children
            .slice(range[0], range[1])
            .map(c => c?.handle)
            .filter(Boolean),
        }))
        .filter(s => s.title && s.body.length);
    },
    toggle(cardIdx, sectionIdx) {
      const current = this.open[cardIdx] || [];
      const next = current.includes(sectionIdx) ? [] : [sectionIdx];
      this.open = {...this.open, [cardIdx]: next};
    },
    isOpen(cardIdx, sectionIdx) {
      return (this.open[cardIdx] || []).includes(sectionIdx);
    },
    normalize(text) {
      return text.toLowerCase().trim().replace(/\s+/g, " ");
    },
    sortByOrders(tags) {
      if (!this.orders || !this.orders.trim()) return tags;
      const orderArray = this.orders
        .toLowerCase()
        .split(",")
        .map(i => this.normalize(i))
        .filter(Boolean);
      if (!orderArray.length) return tags;
      const indexMap = orderArray.reduce((acc, o, i) => {
        acc[o] = i;
        return acc;
      }, {});
      const mapped = tags.map(tag => ({
        tag,
        index:
          indexMap[this.normalize(tag.title)] !== undefined
            ? indexMap[this.normalize(tag.title)]
            : Number.MAX_VALUE,
      }));
      const sorted = mapped.sort((a, b) => a.index - b.index).map(m => m.tag);
      const remaining = tags.filter(t => !sorted.includes(t));
      return [...sorted, ...remaining];
    },
  },
};
</script>
